import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import ProductServiceService from "../Product/ProductService.service";
import CategoryServiceService from "../Category/CategoryService.service";

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import './StockWarning.css';

const NoStock = () => {
    const [productList, setProductList] = useState({ data: [] });
    const [categoryList, setCategoryList] = useState([]);
    const [categoryId, setCategoryId] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        ProductServiceService.fetchOutOfStock(0)
            .then(response => setProductList(response.data))
            .catch(error => console.log("error", error));

        CategoryServiceService.getAll()
            .then(response => setCategoryList(response.data))
            .catch(error => console.log("error", error));
    }, []);

    const fetchProducts = () => {
        setLoading(true);
        ProductServiceService.fetchOutOfStock(categoryId)
            .then(response => setProductList(response.data))
            .catch(error => console.log("error", error))
            .finally(() => setLoading(false));
    };

    const products = Array.isArray(productList.data) ? productList.data : [];
    const pendingOrderCount = products.reduce(
        (total, product) => total + (
            Array.isArray(product.pending_orders) ? product.pending_orders.length : 0
        ),
        0
    );

    const formatProductPackage = (product) => {
        if (product.quantity == null || product.weight == null) {
            return 'Package not specified';
        }

        if (product.quantity === 1) {
            return `${product.weight}${product.variation || ''}`;
        }

        const unitWeight = product.weight / product.quantity;
        return `${product.quantity} × ${Number.isInteger(unitWeight) ? unitWeight : unitWeight.toPrecision(2)}${product.variation || ''}`;
    };

    const sumPendingOrderQuantities = (pendingOrders) => {
        const totalsByUnit = pendingOrders.reduce((totals, pendingOrder) => {
            const quantityMatch = String(pendingOrder.quantity || '').trim().match(/^(-?\d+(?:\.\d+)?)\s*(.*)$/);
            if (!quantityMatch) return totals;

            const unit = quantityMatch[2].trim().toUpperCase();
            totals[unit] = (totals[unit] || 0) + Number(quantityMatch[1]);
            return totals;
        }, {});

        const totals = Object.entries(totalsByUnit).map(([unit, amount]) =>
            `${amount.toLocaleString()}${unit ? ` ${unit}` : ''}`
        );
        return totals.length > 0 ? totals.join(' + ') : 'Not specified';
    };

    const formatDate = (date) => {
        if (!date) return 'Not recorded';
        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return 'Not recorded';

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit'
        }).format(parsedDate);
    };

    return (
        <div className="stock-warning-page">
            <section className="stock-warning-hero stock-warning-hero--out">
                <div className="stock-warning-hero__icon">
                    <ErrorOutlineRoundedIcon />
                </div>
                <div className="stock-warning-hero__copy">
                    <span className="stock-warning-eyebrow">Critical inventory monitor</span>
                    <h1>Out of Stock</h1>
                    <p>Track unavailable products and confirm whether replacement stock is already on order.</p>
                </div>
                <div className="stock-warning-summary">
                    <div className="stock-warning-summary__item">
                        <Inventory2OutlinedIcon />
                        <div><strong>{products.length}</strong><span>Out-of-stock products</span></div>
                    </div>
                    <div className="stock-warning-summary__item">
                        <LocalShippingOutlinedIcon />
                        <div><strong>{pendingOrderCount}</strong><span>Pending orders</span></div>
                    </div>
                </div>
            </section>

            <section className="stock-warning-filter">
                <div>
                    <span className="stock-warning-filter__label">Filter inventory</span>
                    <p>Choose a category to narrow the out-of-stock list.</p>
                </div>
                <div className="stock-warning-filter__controls">
                    <FormControl size="small" className="stock-warning-category">
                        <InputLabel id="no-stock-category-label">Category</InputLabel>
                        <Select
                            labelId="no-stock-category-label"
                            value={categoryId}
                            label="Category"
                            onChange={event => setCategoryId(event.target.value)}
                        >
                            <MenuItem value={0}>All categories</MenuItem>
                            {categoryList.map(category => (
                                <MenuItem value={category.id} key={category.id}>
                                    {category.category_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={fetchProducts}
                        startIcon={<SearchIcon />}
                        className="stock-warning-search"
                    >
                        {loading ? 'Loading...' : 'Apply filter'}
                    </Button>
                </div>
                {loading && <LinearProgress color="warning" className="stock-warning-progress" />}
            </section>

            <section className="stock-warning-table-card">
                <div className="stock-warning-table-card__header">
                    <div>
                        <h2>Products requiring restock</h2>
                        <p>{products.length} {products.length === 1 ? 'product is' : 'products are'} currently unavailable.</p>
                    </div>
                    <span className="stock-warning-critical stock-warning-critical--header">
                        <ErrorOutlineRoundedIcon />Immediate attention
                    </span>
                </div>

                <div className="table-responsive">
                    <table className="stock-warning-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Category</th>
                                <th>Status</th>
                                <th>Current stock</th>
                                <th>Last stock date</th>
                                <th>Pending supplier orders</th>
                                <th aria-label="Actions"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.length > 0 ? products.map(product => (
                                <tr key={product.id}>
                                    <td>
                                        <div className="stock-warning-product">
                                            <span className="stock-warning-product__avatar stock-warning-product__avatar--out">
                                                {product.product_name ? product.product_name.charAt(0).toUpperCase() : '?'}
                                            </span>
                                            <div>
                                                <strong>{product.product_name}</strong>
                                                <span>#{product.id} · {product.brand_name || 'No brand'}</span>
                                                <span className="stock-warning-package">{formatProductPackage(product)}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className="stock-warning-category-pill">{product.category_name}</span></td>
                                    <td><span className="stock-warning-out-pill">Out of stock</span></td>
                                    <td>
                                        <div className="stock-warning-levels">
                                            <div><span>Wholesale</span><strong>{product.stock ?? 0}</strong></div>
                                            <div><span>Pieces</span><strong>{product.stock_pc ?? 0}</strong></div>
                                        </div>
                                    </td>
                                    <td><span className="stock-warning-date">{formatDate(product.updated_at)}</span></td>
                                    <td className="stock-warning-orders-cell">
                                        {Array.isArray(product.pending_orders) && product.pending_orders.length > 0 ? (
                                            <div className="stock-warning-orders">
                                                {product.pending_orders.length > 1 && (
                                                    <div className="stock-warning-orders__summary">
                                                        <span>{product.pending_orders.length} pending orders</span>
                                                        <div>
                                                            <small>Total incoming</small>
                                                            <strong>{sumPendingOrderQuantities(product.pending_orders)}</strong>
                                                        </div>
                                                    </div>
                                                )}
                                                {product.pending_orders.map(order => (
                                                    <Link
                                                        to={"/orderSupplierApproval/" + order.order_supplier_transaction_id}
                                                        className="stock-warning-order"
                                                        key={order.order_supplier_transaction_id}
                                                    >
                                                        <div className="stock-warning-order__icon"><LocalShippingOutlinedIcon /></div>
                                                        <div className="stock-warning-order__details">
                                                            <strong>{order.supplier}</strong>
                                                            <span>PO #{order.order_supplier_transaction_id} · {order.date}</span>
                                                            <span className={`stock-warning-order__status stock-warning-order__status--${String(order.status || 'PENDING').toLowerCase()}`}>
                                                                {String(order.status || 'PENDING').replaceAll('_', ' ')}
                                                            </span>
                                                        </div>
                                                        <div className="stock-warning-order__quantity">
                                                            <span>Incoming</span><strong>{order.quantity}</strong>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="stock-warning-no-orders">
                                                <LocalShippingOutlinedIcon /><span>No pending supplier order</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="stock-warning-actions">
                                        <div className="stock-warning-action-stack">
                                            <Link to={"/viewTransaction/" + product.id} className="stock-warning-history-link">
                                                Stock history
                                            </Link>
                                            <Link to={"/viewOutOfStockHistory/" + product.id} className="stock-warning-history-link">
                                                OOS history
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="7">
                                        <div className="stock-warning-empty">
                                            <Inventory2OutlinedIcon />
                                            <h3>No out-of-stock products</h3>
                                            <p>There are no unavailable products in this category.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default NoStock;
